import React, { useEffect } from 'react';
import { Form, InputNumber, Select, Button, Card, Typography, Alert, Row, Col, Input, DatePicker, Divider, List, Space } from 'antd';
import { DeleteOutlined, ScanOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { CustomAttributeRenderer } from '@components/operator/shared/custom_attribute_renderer';
import { useSerialNumberInput } from '@/hooks/useSerialNumberInput';
import type { VerificationFormPanelProps, VerificationFormValues } from './types';

// Local boundary type for what Form#onFinish hands us: dates are still Dayjs;
// every other field already matches VerificationFormValues. Kept local because
// it's an implementation detail of the panel, not part of the public contract.
type RawFormValues = Omit<VerificationFormValues, 'expiration_date' | 'manufacturing_date'> & {
  expiration_date?: Dayjs | null;
  manufacturing_date?: Dayjs | null;
};

const { Text } = Typography;

type QuantityStatus = 'none' | 'exact' | 'over' | 'short';

const getQuantityStatus = (
  inputQty: number,
  scheduledQty: number | undefined,
): { status: QuantityStatus; color: string } => {
  if (inputQty === 0) return { status: 'none', color: '#999' };
  if (inputQty === scheduledQty) return { status: 'exact', color: '#52c41a' };
  if (scheduledQty !== undefined && inputQty > scheduledQty) return { status: 'over', color: '#faad14' };
  return { status: 'short', color: '#ff4d4f' };
};

const VerificationFormPanel: React.FC<VerificationFormPanelProps> = ({
  productFlags,
  initialValues,
  initialSerials,
  quantityMode,
  scheduledQuantity,
  baselineReceivedQuantity,
  attributeDefinitions,
  inventoryTypes,
  showNote,
  submitButtonLabel,
  cancelButtonLabel,
  onSubmit,
  onCancel,
  loading,
  disabled,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const resolvedSubmitLabel = submitButtonLabel ?? t('operator.verification_form.confirm_button', { defaultValue: '確認驗收' });
  const resolvedCancelLabel = cancelButtonLabel ?? t('operator.verification_form.cancel_button', { defaultValue: '取消' });

  const hasValidity = (productFlags.validity_days ?? 0) > 0;
  const hasMfgRequired = !!productFlags.manufacturing_date_required;
  const hasBatchRequired = !!productFlags.batch_management_required;

  const boxCount = Form.useWatch<number | undefined>('box_count', form);
  const quantityPerBox = Form.useWatch<number | undefined>('quantity_per_box', form);
  const looseQuantity = Form.useWatch<number | undefined>('loose_quantity', form);
  const calculatedQuantity = ((boxCount || 0) * (quantityPerBox || 0)) + (looseQuantity || 0);

  const singleQuantity = Form.useWatch<number | undefined>('quantity', form);
  const computedQuantity = quantityMode === 'box-calc' ? calculatedQuantity : (singleQuantity ?? 0);

  const {
    input: serialInput,
    setInput: setSerialInput,
    scannedSerials,
    error: serialError,
    submit: submitSerial,
    removeAt: removeSerialAt,
    replaceSerials,
    inputRef: serialInputRef,
    isCompleted: serialsComplete,
    totalRequired: totalSerialsNeeded,
  } = useSerialNumberInput({
    productFlags: {
      required_serial_numbers_count: productFlags.required_serial_numbers_count,
      serial_number_pattern: productFlags.serial_number_pattern ?? null,
      box_serial_number_delimiter: productFlags.box_serial_number_delimiter ?? null,
      product_barcode: productFlags.product_barcode ?? null,
    },
    scanQuantity: computedQuantity,
    initialSerials,
  });

  // 「是否需要序號」由 required_serial_numbers_count > 0 推導；hook 內部用 count
  // 判斷，FE 在 UI / 條件分支上也對齊同一個推導。
  const needSerialNumber = (productFlags.required_serial_numbers_count ?? 0) > 0;

  // Quantity-shrink truncation: when computedQuantity decreases below the
  // current scanned count, drop the trailing excess. The hook doesn't do this
  // automatically (it only enforces upper bounds at submit time).
  //
  // Skip when computedQuantity is 0/falsy: that's the transient state while
  // the user clears the input mid-edit (before they re-type the new value).
  // If we truncated to 0 here we'd wipe everything during the clear, then
  // the user's intended new total (e.g. 1) would arrive too late to recover.
  useEffect(() => {
    if (!needSerialNumber) return;
    if (computedQuantity <= 0) return;
    const requiredPerUnit = productFlags.required_serial_numbers_count ?? 1;
    const newTotal = computedQuantity * requiredPerUnit;
    if (scannedSerials.length > newTotal) {
      replaceSerials(scannedSerials.slice(0, newTotal));
    }
    // scannedSerials / replaceSerials intentionally omitted: re-running on
    // every scan would loop with the setState inside replaceSerials.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedQuantity, needSerialNumber, productFlags.required_serial_numbers_count]);

  useEffect(() => {
    const sharedFields = {
      batch: initialValues.batch ?? null,
      expiration_date: initialValues.expiration_date ? dayjs(initialValues.expiration_date) : null,
      manufacturing_date: initialValues.manufacturing_date ? dayjs(initialValues.manufacturing_date) : null,
      inventory_type_id: initialValues.inventory_type_id,
      custom_attributes: initialValues.custom_attributes ?? {},
      note: initialValues.note ?? '',
    };
    if (quantityMode === 'box-calc') {
      form.setFieldsValue({
        quantity: initialValues.quantity ?? 0,
        box_count: initialValues.box_count ?? 0,
        quantity_per_box: initialValues.quantity_per_box ?? 0,
        loose_quantity: initialValues.loose_quantity ?? 0,
        ...sharedFields,
      });
    } else {
      form.setFieldsValue({
        quantity: initialValues.quantity ?? 1,
        ...sharedFields,
      });
    }
    // custom_attributes intentionally omitted: object refs would clobber user edits on parent re-render
  }, [
    form,
    quantityMode,
    initialValues.quantity,
    initialValues.inventory_type_id,
    initialValues.box_count,
    initialValues.quantity_per_box,
    initialValues.loose_quantity,
    initialValues.batch,
    initialValues.expiration_date,
    initialValues.manufacturing_date,
    initialValues.note,
  ]);

  const calculateTotal = () => {
    const bc = form.getFieldValue('box_count') || 0;
    const qpb = form.getFieldValue('quantity_per_box') || 0;
    const loose = form.getFieldValue('loose_quantity') || 0;
    form.setFieldsValue({ quantity: (bc * qpb) + loose });
  };

  const handleFinish = async (raw: RawFormValues) => {
    const values: VerificationFormValues = {
      ...raw,
      expiration_date: raw.expiration_date ? raw.expiration_date.format('YYYY-MM-DD') : null,
      manufacturing_date: raw.manufacturing_date ? raw.manufacturing_date.format('YYYY-MM-DD') : null,
    };
    await onSubmit(values, scannedSerials);
  };

  // Compare the cumulative total (already-received baseline + this batch)
  // against the scheduled total, so add_batch / edit don't false-flag
  // over/short by comparing a single batch against the whole plan.
  const status = getQuantityStatus((baselineReceivedQuantity ?? 0) + calculatedQuantity, scheduledQuantity);
  const showStatusBar = scheduledQuantity !== undefined;

  return (
    <div data-testid="verification-form-panel">
      <div style={{ display: 'flex', gap: 24 }}>
        <div style={{ flex: 1 }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            disabled={disabled}
          >
            {quantityMode === 'single' && (
              <Form.Item
                name="quantity"
                label={t('operator.verification_form.quantity', { defaultValue: '數量' })}
                rules={[{ required: true, type: 'number', min: 1 }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            )}

            {quantityMode === 'box-calc' && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <Form.Item name="box_count" style={{ margin: 0, flex: 1 }}>
                    <InputNumber min={0} style={{ width: '100%', height: 50 }} onChange={calculateTotal} size="large" />
                  </Form.Item>
                  <Text type="secondary">{t('operator.verification_form.box_count', { defaultValue: '箱' })}</Text>
                  <Text style={{ fontSize: 20 }}>×</Text>
                  <Form.Item name="quantity_per_box" style={{ margin: 0, flex: 1 }}>
                    <InputNumber min={0} style={{ width: '100%', height: 50 }} onChange={calculateTotal} size="large" />
                  </Form.Item>
                  <Text type="secondary">{t('operator.verification_form.quantity_per_box', { defaultValue: '入' })}</Text>
                  <Text style={{ fontSize: 20 }}>+</Text>
                  <Form.Item name="loose_quantity" style={{ margin: 0, flex: 1 }}>
                    <InputNumber min={0} style={{ width: '100%', height: 50 }} onChange={calculateTotal} size="large" />
                  </Form.Item>
                  <Text type="secondary">{t('operator.verification_form.loose_quantity', { defaultValue: '散' })}</Text>
                </div>
                {showStatusBar && (
                  <div
                    data-testid="quantity-status"
                    data-status={status.status}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderRadius: 6,
                      background:
                        status.status === 'exact' ? '#f6ffed'
                          : status.status === 'over' ? '#fffbe6'
                            : status.status === 'short' ? '#fff2f0'
                              : '#fafafa',
                      border: `1px solid ${
                        status.status === 'exact' ? '#b7eb8f'
                          : status.status === 'over' ? '#ffe58f'
                            : status.status === 'short' ? '#ffccc7'
                              : '#d9d9d9'
                      }`,
                    }}
                  >
                    <Text>{t('operator.verification_form.verify_quantity', { defaultValue: '驗收數量' })}</Text>
                    <Text strong style={{ fontSize: 28, color: status.color }}>{calculatedQuantity}</Text>
                  </div>
                )}
                <Form.Item name="quantity" hidden><InputNumber /></Form.Item>
                {showStatusBar && status.status === 'over' && (
                  <Text type="danger" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                    {t('operator.verification_form.over_received_warning', { defaultValue: '驗收數量超過預計數量' })}
                  </Text>
                )}
                {showStatusBar && status.status === 'short' && (
                  <Alert type="error" message={t('operator.verification_form.short_received_warning', { defaultValue: '驗收數量少於預計數量' })} showIcon style={{ marginTop: 12 }} />
                )}
              </Card>
            )}

            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  label={t('operator.verification_form.batch', { defaultValue: '批號' })}
                  name="batch"
                  rules={hasBatchRequired ? [{ required: true, message: t('common.validation.required') as string }] : []}
                >
                  <Input placeholder={t('operator.verification_form.batch_placeholder', { defaultValue: '請輸入批號' }) as string} />
                </Form.Item>
              </Col>
              {hasValidity && (
                <Col span={12}>
                  <Form.Item
                    label={t('operator.verification_form.expiration_date', { defaultValue: '有效日期' })}
                    name="expiration_date"
                    rules={[{ required: true, message: t('common.validation.required') as string }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
              )}
            </Row>

            {hasMfgRequired && (
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    label={t('operator.verification_form.manufacturing_date', { defaultValue: '製造日期' })}
                    name="manufacturing_date"
                    rules={[{ required: true, message: t('common.validation.required') as string }]}
                  >
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Form.Item
              name="inventory_type_id"
              label={t('operator.verification_form.inventory_type', { defaultValue: 'Inventory type' })}
              rules={[{ required: true, message: t('common.validation.required') as string }]}
            >
              <Select
                options={inventoryTypes.map(it => ({ label: it.name, value: it.id }))}
                placeholder={t('operator.verification_form.inventory_type_placeholder', { defaultValue: 'Select inventory type' }) as string}
              />
            </Form.Item>

            {showNote && (
              <Row gutter={12}>
                <Col span={24}>
                  <Form.Item label={t('operator.verification_form.note', { defaultValue: '備註' })} name="note" style={{ marginBottom: 12 }}>
                    <Input.TextArea rows={2} placeholder={t('operator.verification_form.note_placeholder', { defaultValue: '請輸入備註（選填）' }) as string} />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {attributeDefinitions.length > 0 && (
              <>
                <Divider dashed style={{ margin: '12px 0' }}>{t('operator.verification_form.custom_attributes_section', { defaultValue: '庫存自訂屬性' })}</Divider>
                <CustomAttributeRenderer attributeDefinitions={attributeDefinitions} />
              </>
            )}
          </Form>

          {/* Button row lives OUTSIDE <Form> on purpose: AntD's Form-level
              disabled cascades into descendant Buttons, which would lock the
              Cancel (exit) button whenever the parent passes disabled=true.
              Cancel is an exit control, not data entry — it must stay clickable. */}
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            {onCancel && (
              <Button onClick={onCancel} disabled={loading}>
                {resolvedCancelLabel}
              </Button>
            )}
            <Button
              type="primary"
              onClick={() => form.submit()}
              loading={loading}
              disabled={
                loading ||
                disabled ||
                (needSerialNumber && !serialsComplete) ||
                computedQuantity <= 0
              }
            >
              {resolvedSubmitLabel}
            </Button>
          </div>
        </div>

        {needSerialNumber && (
          <div style={{ width: 260, borderLeft: '1px solid #f0f0f0', paddingLeft: 24 }}>
            <Typography.Title level={5} style={{ marginTop: 0 }}>
              <Space><ScanOutlined />{t('operator.verification_form.serial_section_title', { defaultValue: '序號掃描' })}</Space>
            </Typography.Title>

            <Alert
              message={
                <Space>
                  <span>{t('operator.verification_form.serial_count_needed', { defaultValue: '所需數量:' })}</span>
                  <Typography.Text strong>{totalSerialsNeeded}</Typography.Text>
                  <span style={{ fontSize: 12, color: '#666' }}>
                    ({computedQuantity} x {productFlags.required_serial_numbers_count ?? 1})
                  </span>
                </Space>
              }
              type={serialsComplete ? 'success' : 'warning'}
              showIcon
              style={{ marginBottom: 12 }}
            />

            <Input
              ref={serialInputRef}
              prefix={<ScanOutlined style={{ color: '#1890ff' }} />}
              placeholder={t('operator.verification_form.scan_serial_placeholder', { defaultValue: '請掃描或輸入序號...' }) as string}
              value={serialInput}
              onChange={e => setSerialInput(e.target.value)}
              onPressEnter={() => submitSerial()}
              disabled={serialsComplete}
              autoComplete="off"
              spellCheck={false}
              size="large"
              style={{ marginBottom: 8 }}
            />

            {serialError && <Alert message={serialError} type="error" showIcon style={{ marginTop: 8 }} />}

            <div style={{ marginTop: 16, height: 300, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
              <List
                size="small"
                dataSource={scannedSerials}
                renderItem={(item: string, index: number) => (
                  <List.Item
                    actions={[
                      <Button
                        key="del"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeSerialAt(index)}
                      />,
                    ]}
                  >
                    <Space>
                      <Typography.Text type="secondary" style={{ width: 24 }}>{index + 1}.</Typography.Text>
                      <Typography.Text strong>{item}</Typography.Text>
                    </Space>
                  </List.Item>
                )}
                locale={{ emptyText: t('operator.verification_form.no_serials_scanned', { defaultValue: '尚未掃描序號' }) }}
              />
            </div>

            <div style={{ marginTop: 8, textAlign: 'right' }}>
              <Typography.Text type="secondary">
                {t('operator.verification_form.total_scanned', { defaultValue: '已掃描' })}: <Typography.Text strong>{scannedSerials.length}</Typography.Text> / {totalSerialsNeeded}
              </Typography.Text>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerificationFormPanel;
